import { Facebook, Linkedin, Instagram, X } from "lucide-react";
import { useLocation } from 'react-router-dom';
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const Footer = () => {
  const location = useLocation();
  const { t } = useTranslation();

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      window.location.href = `/${sectionId}`;
      return;
    }
    const element = document.getElementById(sectionId.replace('#', ''));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#424242] text-white relative">
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute inset-0 w-full h-full opacity-10"
          style={{
            backgroundImage: "url('/MK-Pattern.svg')",
            backgroundSize: "100% auto",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            transform: "scale(1)",
            transformOrigin: "center",
            maxWidth: "100vw",
            maxHeight: "100%",
          }}
          animate={{
            scale: [1, 1.05, 1],
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">

          {/* Logo & Description */}
          <div>
            <img src="/Logo/MK-Logo-Footer.svg" alt="MK Information System Logo" className="w-32 mb-4" />
            <p className="text-[#F5F5F5]">{t("footer.description")}</p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold text-[#D4AF37] mb-4">{t("footer.quickLinks")}</h4>
            <ul className="space-y-2">
              <li><a href="/" className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">{t("footer.home")}</a></li>
              <li>
                <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection("services"); }} className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">
                  {t("footer.services")}
                </a>
              </li>
              <li>
                <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection("about"); }} className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">
                  {t("footer.about")}
                </a>
              </li>
              <li>
                <a href="#Blogs" onClick={(e) => { e.preventDefault(); scrollToSection("Blogs"); }} className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">
                  {t("footer.blogs")}
                </a>
              </li>
              <li>
                <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection("contact"); }} className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">
                  {t("footer.contact")}
                </a>
              </li>
            </ul>
          </div>

          {/* Services & Solutions */}
          <div>
            <h4 className="text-lg font-semibold text-[#D4AF37] mb-4">{t("footer.servicesTitle")}</h4>
            <ul className="space-y-2">
              <li>
                <a href="/services/cybersecurity" className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">
                 {t("footer.cybersecurity")}
                </a>
              </li>
              <li>
                <a href="/services/networking-connectivity" className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">
               {t("footer.networking")}
                </a>
              </li>
              <li>
                <a href="/services/AI-solutions" className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">
                {t("footer.ai")}
                </a>
              </li>
              <li>
                <a href="/services/software-solutions" className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">
                {t("footer.software")}
                </a>
              </li>
              <li>
                <a href="/services/telecommunication-solutions" className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">
               {t("footer.telecom")}
                </a>
              </li>
              <li>
                <a href="/services/consulting-solutions" className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">
                {t("footer.consulting")}
                </a>
              </li>
            </ul>
          </div>


          {/* Social Media */}
          <div>
            <h4 className="text-lg font-semibold text-[#D4AF37] mb-4">{t("footer.connectWithUs")}</h4>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/MKinfoSystems" target="_blank" rel="noopener noreferrer" className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">
                <Facebook className="w-6 h-6" />
              </a>
              <a href="https://x.com/MKinfoSystems" target="_blank" rel="noopener noreferrer" className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">
                <X className="w-6 h-6" />
              </a>
              <a href="https://www.linkedin.com/company/mk-info-systems/?viewAsMember=true" target="_blank" rel="noopener noreferrer" className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">
                <Linkedin className="w-6 h-6" />
              </a>
              <a href="https://www.instagram.com/mkinfosystems/" target="_blank" rel="noopener noreferrer" className="text-[#F5F5F5] hover:text-[#B8945C] transition-colors">
                <Instagram className="w-6 h-6" />
              </a>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="mt-8 pt-8 border-t border-[#B8945C] text-center text-[#F5F5F5]">
          <p>&copy; {new Date().getFullYear()} {t("footer.copyright")}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
